import {
    useRef,
    useState
} from 'react';

import { SimpleGrid, Text } from '@mantine/core';

import { useCustomers } from '../hooks/useCustomers.ts';
import { useCustomersSelection } from '../hooks/useCustomersSelection.ts';
import { FormDrawer } from '../drawers/FormDrawer.tsx';
import { update } from '../services/CustomersService.ts';
import {
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import { DataTableColumn } from "mantine-datatable";
import { DataTableTokenPagination } from "../../../components/DataTableTokenPagination.tsx";
import { Customer, CustomerStatus } from '../types';
import { ArchiveRecordButton } from '../buttons/ArchiveRecordButton.tsx';
import { CustomerStatusBadge } from '../profile/CustomerStatusBadge.tsx';
import { useQueryClient } from '@tanstack/react-query';

interface RecordsTableProps {
    statusFilter?: CustomerStatus | null;
}

export const RecordsTable = ({ statusFilter }: RecordsTableProps) => {
    const {selectedRecords, setSelectedRecords} = useCustomersSelection();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<Customer | null>(null);
    const queryClient = useQueryClient();

    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useCustomers(statusFilter);

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const handleRecordClick = (record: Customer) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const handleDrawerSubmit = async (values: Customer) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, values);
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            queryClient.invalidateQueries({ queryKey: ['all-customers'] });
        }
    };

    const columns: DataTableColumn<Customer>[] = [
        {
            accessor: 'name',
            title: 'Customer',
            render: (record: Customer) => (
                <Text
                    c="blue"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRecordClick(record)}
                >
                    {record.name}
                </Text>
            )
        },
        { accessor: 'status', title: 'Status', render: (record: Customer) => (
            <CustomerStatusBadge status={record.status} />
        ) },
        { accessor: 'created', title: 'Created' },
        {
            accessor: 'actions',
            title: 'Actions',
            textAlign: 'center',
            render: (record: Customer) => (
                <ArchiveRecordButton customer={record} />
            )
        },
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination totalCount={totalCount.toString()} nextToken={nextToken} handleLoadMore={fetchRecords} />

            <FormDrawer
                record={selectedRecord || {} as Customer}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
